import { useState, useEffect, useMemo } from 'react';
import { listOrgCollection } from '../../storage/FileSystemAdapter';
import { COUNTRIES } from '../../utils/countries';
import { SUBREGIONS } from '../../utils/subregions';
import {
  BG_SURFACE, BG_SHELL, BORDER_DEFAULT,
  TEXT_HEADING, TEXT_DEFAULT, TEXT_MUTED,
  ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER,
  FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_STAT_VALUE,
} from '../../theme';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    loading:                 'Cargando…',
    emptyNoCustomers:        'No hay clientes registrados aún.',
    emptyNoCountries:        'Ninguno de tus clientes tiene sites con país especificado. Edita tus clientes para añadir sites con país.',
    statJurisdictions:       'Jurisdicciones',
    statCountries:           'Países',
    statCustomers:           'Clientes',
    statWithoutCoverage:     'Sin cobertura legal',
    customersWithSites:      '{n} clientes · {m} sites',
    unitsSuffix:             ' · {k} unidades',
    noObligation:            'Sin obligación legal registrada',
    customersHeader:         'Clientes',
    legalHeader:             'Obligaciones legales registradas',
  },
  en: {
    loading:                 'Loading…',
    emptyNoCustomers:        'No customers recorded yet.',
    emptyNoCountries:        'None of your customers has sites with a country yet. Edit your customers to add sites with a country.',
    statJurisdictions:       'Jurisdictions',
    statCountries:           'Countries',
    statCustomers:           'Customers',
    statWithoutCoverage:     'Without legal coverage',
    customersWithSites:      '{n} customers · {m} sites',
    unitsSuffix:             ' · {k} units',
    noObligation:            'No legal obligation recorded',
    customersHeader:         'Customers',
    legalHeader:             'Recorded legal obligations',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function resolveCountryName(code, lang) {
  const c = COUNTRIES.find((x) => x.code === code);
  if (!c) return code;
  return lang === 'es' ? c.nameEs : c.nameEn;
}

function resolveSubregionName(countryCode, subCode, lang) {
  const list = SUBREGIONS[countryCode];
  if (Array.isArray(list)) {
    const item = list.find((x) => x.code === subCode);
    if (item) return lang === 'es' ? item.nameEs : item.nameEn;
  }
  return subCode;
}

// Returns the raw chip code rendered next to the title. For subregions that
// come from the SUBREGIONS catalogue (US, CA) the code itself is already in
// `<country>-<sub>` form. For free-text subregions in other countries we
// render `<country> · <subregion>`.
function buildJurisdictionChip(country, subregion) {
  if (!subregion) return country;
  const list = SUBREGIONS[country];
  if (Array.isArray(list) && list.some((x) => x.code === subregion)) {
    return subregion;
  }
  return `${country} · ${subregion}`;
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function JurisdictionsView({ customers, lang, org, onOpenCustomer, onNavigate }) {
  const t = T[lang] || T.en;

  const [legalEntries, setLegalEntries] = useState([]);

  useEffect(() => {
    listOrgCollection(org.id, 'legal-register')
      .then((items) => setLegalEntries(Array.isArray(items) ? items : []))
      .catch(() => setLegalEntries([]));
  }, [org.id]);

  // ── Aggregate jurisdictions ─────────────────────────────────────────────────
  const aggregation = useMemo(() => {
    const safeCustomers = Array.isArray(customers) ? customers : [];
    const jurisdictionMap = new Map();
    for (const c of safeCustomers) {
      const sites = Array.isArray(c.sites) ? c.sites : [];
      for (const s of sites) {
        if (!s || !s.country) continue;
        const key = s.subregion ? `${s.country}::${s.subregion}` : s.country;
        let entry = jurisdictionMap.get(key);
        if (!entry) {
          entry = {
            key,
            country: s.country,
            subregion: s.subregion || null,
            customerIds: new Set(),
            siteCount: 0,
            unitCount: 0,
          };
          jurisdictionMap.set(key, entry);
        }
        entry.customerIds.add(c.id);
        entry.siteCount += 1;
        entry.unitCount += Array.isArray(s.units) ? s.units.length : 0;
      }
    }
    return jurisdictionMap;
  }, [customers]);

  // ── Cross-reference with LegalRegister ─────────────────────────────────────
  const jurisdictions = useMemo(() => {
    const list = [];
    for (const j of aggregation.values()) {
      const lrEntries = legalEntries.filter((lr) => {
        const ids = Array.isArray(lr.triggeringCustomerIds) ? lr.triggeringCustomerIds : [];
        return ids.some((id) => j.customerIds.has(id));
      });
      list.push({
        ...j,
        countryLabel: resolveCountryName(j.country, lang),
        subregionLabel: j.subregion ? resolveSubregionName(j.country, j.subregion, lang) : null,
        chip: buildJurisdictionChip(j.country, j.subregion),
        lrEntries,
      });
    }
    list.sort((a, b) => {
      const cmp = a.countryLabel.localeCompare(b.countryLabel);
      if (cmp !== 0) return cmp;
      if (!a.subregion && b.subregion) return -1;
      if (a.subregion && !b.subregion) return 1;
      if (!a.subregion && !b.subregion) return 0;
      return (a.subregionLabel || '').localeCompare(b.subregionLabel || '');
    });
    return list;
  }, [aggregation, legalEntries, lang]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const totalJurisdictions = jurisdictions.length;
  const distinctCountries = new Set(jurisdictions.map((j) => j.country)).size;
  const distinctCustomers = (() => {
    const ids = new Set();
    for (const j of jurisdictions) j.customerIds.forEach((id) => ids.add(id));
    return ids.size;
  })();
  const withoutCoverage = jurisdictions.filter((j) => j.lrEntries.length === 0).length;

  // ── Render helpers ──────────────────────────────────────────────────────────
  const customersById = useMemo(() => {
    const map = new Map();
    const safeCustomers = Array.isArray(customers) ? customers : [];
    for (const c of safeCustomers) map.set(c.id, c);
    return map;
  }, [customers]);

  // ── Empty states ───────────────────────────────────────────────────────────
  if (customers === null || customers === undefined) {
    return (
      <p style={{ ...FONT_LABEL, color: TEXT_MUTED, margin: '1rem 0' }}>{t.loading}</p>
    );
  }
  const safeCustomers = Array.isArray(customers) ? customers : [];
  if (safeCustomers.length === 0) {
    return (
      <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
        <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: 0 }}>{t.emptyNoCustomers}</p>
      </div>
    );
  }
  if (jurisdictions.length === 0) {
    return (
      <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '2.5rem', textAlign: 'center' }}>
        <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: 0 }}>{t.emptyNoCountries}</p>
      </div>
    );
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const statBox = {
    background: BG_SURFACE,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 10,
    padding: '0.9rem 1.25rem',
    minWidth: 140,
  };
  const statLabel = {
    ...FONT_SMALL,
    color: TEXT_DEFAULT,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  };
  const chipBtnSt = {
    ...FONT_LABEL,
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.25rem 0.55rem',
    background: BG_SHELL,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 6,
    color: TEXT_DEFAULT,
    cursor: 'pointer',
  };
  const isoChipSt = {
    ...FONT_TINY,
    padding: '0.15rem 0.45rem',
    background: BG_SHELL,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 5,
    color: TEXT_MUTED,
    letterSpacing: '0.03em',
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Summary bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={statBox}>
          <div style={statLabel}>{t.statJurisdictions}</div>
          <div style={{ ...FONT_STAT_VALUE, color: TEXT_HEADING }}>{totalJurisdictions}</div>
        </div>
        <div style={statBox}>
          <div style={statLabel}>{t.statCountries}</div>
          <div style={{ ...FONT_STAT_VALUE, color: TEXT_HEADING }}>{distinctCountries}</div>
        </div>
        <div style={statBox}>
          <div style={statLabel}>{t.statCustomers}</div>
          <div style={{ ...FONT_STAT_VALUE, color: TEXT_HEADING }}>{distinctCustomers}</div>
        </div>
        <div style={statBox}>
          <div style={statLabel}>{t.statWithoutCoverage}</div>
          <div style={{ ...FONT_STAT_VALUE, color: withoutCoverage > 0 ? ERROR_TEXT : TEXT_HEADING }}>{withoutCoverage}</div>
        </div>
      </div>

      {/* Jurisdiction cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {jurisdictions.map((j) => {
          const empty = j.lrEntries.length === 0;
          const titleLabel = j.subregionLabel
            ? `${j.countryLabel} — ${j.subregionLabel}`
            : j.countryLabel;
          const countsLine = (() => {
            const base = t.customersWithSites
              .replace('{n}', String(j.customerIds.size))
              .replace('{m}', String(j.siteCount));
            return j.unitCount > 0
              ? base + t.unitsSuffix.replace('{k}', String(j.unitCount))
              : base;
          })();
          const cardSt = empty ? {
            background: BG_SURFACE,
            border: `1px solid ${BORDER_DEFAULT}`,
            borderLeft: `4px solid ${FIELD_ERROR_BORDER}`,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
            padding: '1rem 1.25rem',
          } : {
            background: BG_SURFACE,
            border: `1px solid ${BORDER_DEFAULT}`,
            borderRadius: 10,
            padding: '1rem 1.25rem',
          };

          return (
            <div key={j.key} style={cardSt}>
              {/* Title row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                <h3 style={{ ...FONT_BODY_STRONG, color: TEXT_HEADING, margin: 0 }}>
                  {titleLabel}
                </h3>
                <span style={isoChipSt}>{j.chip}</span>
              </div>

              {/* Counts line */}
              <p style={{ ...FONT_SMALL, color: TEXT_MUTED, margin: '0 0 0.75rem' }}>
                {countsLine}
              </p>

              {/* Customers */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
                  {t.customersHeader}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {[...j.customerIds].map((cid) => {
                    const c = customersById.get(cid);
                    if (!c) return null;
                    return (
                      <button
                        key={cid}
                        type="button"
                        onClick={() => onOpenCustomer && onOpenCustomer(cid)}
                        style={chipBtnSt}
                        title={c.name}
                      >
                        <span>{c.name}</span>
                        <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>{c.ref || c.id}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Legal register entries */}
              <div>
                <div style={{ ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
                  {t.legalHeader}
                </div>
                {empty ? (
                  <span style={{
                    ...FONT_LABEL,
                    display: 'inline-block',
                    padding: '0.25rem 0.6rem',
                    background: ERROR_BG,
                    border: `1px solid ${ERROR_BORDER}`,
                    borderRadius: 6,
                    color: ERROR_TEXT,
                  }}>
                    {t.noObligation}
                  </span>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {j.lrEntries.map((lr) => (
                      <button
                        key={lr.id}
                        type="button"
                        onClick={() => onNavigate && onNavigate('legal-register', null)}
                        style={chipBtnSt}
                        title={lr.title || lr.ref || lr.id}
                      >
                        <span>{lr.title || lr.ref || lr.id}</span>
                        {lr.ref && lr.title && (
                          <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>{lr.ref}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
